import {
  isDesignIn,
  isRoll,
  isCustomShapeSticker,
  isSticker,
  flattened,
  isImageRemoveWhite,
  isMobileMode,
  isSheetFlow,
  isRollFlow,
} from '@app/utils/utils';
import {
  HELP_SECTION_PRODUCT_TYPE,
  HELP_SECTION_ART_SIZE_POSITION,
  HELP_SECTION_APPLY_CODES,
  HELP_SECTION_UPLOAD_ART,
  HELP_SECTION_WHITE_INK,
  HELP_TOPIC_WHITE_INK,
  HELP_TOPIC_UPLOAD_ART,
  HELP_TOPIC_ART_SIZE_POSITION,
  HELP_TOPIC_APPLY_CODES,
  HELP_TOPIC_TRANSPARENCY_CODES,
  HELP_TOPIC_ART_SIZE_POSITION_STB_LINES,
  HELP_TOPIC_CONTACT_US,
  HELP_TOPIC_ROLL_LABELS,
  HELP_TOPIC_DIE_CUT_STICKERS,
  HELP_TOPIC_STICKER_SIZE,
  HELP_TOPIC_ART_SIZE_POSITION_STRETCHED,
  HELP_TOPIC_ART_SIZE_POSITION_OPTIMIZED,
  HELP_TOPIC_ART_SIZE_POSITION_FLATTENED,
  HELP_TOPIC_ART_SIZE_POSITION_ADJUSTED,
  HELP_TOPIC_STICKER_SIZE_POSITION,
  HELP_TOPIC_WHITE_INK_OPTIONS,
  HELP_TOPIC_WHITE_INK_IN_YOUR_UPLOAD,
  HELP_TOPIC_SHEET_LABELS,
  HELP_TOPIC_UPLOAD_ART_LABEL_SHAPE_YOUR_UPLOAD,
  HELP_TOPIC_SHEET_SIZE,
  HELP_TOPIC_SHAPE_CORNERS_SIZE,
  WORKING_AREA_WARNING_STRETCHED_KEY,
  WORKING_AREA_WARNING_FLATTENED_KEY,
  WORKING_AREA_WARNING_OPTIMIZED_KEY,
  WORKING_AREA_WARNING_ADJUSTED_KEY,
  WORKING_AREA_WARNING_CUSTOM_SHAPE_KEY,
  WORKING_AREA_WARNING_HPI_WHITE_KEY,
  WORKING_AREA_WARNING_CUSTOM_SHAPE_HPI_WHITE_KEY,
  WORKING_AREA_WARNING_OPTIMIZED_CODES,
  WORKING_AREA_WARNING_STRETCHED_CODE,
  WORKING_AREA_WARNING_FLATTENED_CODE,
  WORKING_AREA_WARNING_ADJUSTED_CODE,
  WORKING_AREA_WARNING_CUSTOM_SHAPE_CODE,
  WORKING_AREA_WARNING_HPI_WHITE_CODE,
  WORKING_AREA_WARNING_CUSTOM_SHAPE_HPI_WHITE_CODE,
  HELP_TOPIC_UPLOAD_ART_PREPARE_FILES,
  HELP_TOPIC_PRODUCT_TYPE,
} from '@app/constants';

const productSectionTypeTitleKey = flowConfig => {
  if (isDesignIn(flowConfig)) {
    return HELP_TOPIC_TRANSPARENCY_CODES;
  } else if (isRoll(flowConfig)) {
    return HELP_TOPIC_ROLL_LABELS;
  } else if (isSheetFlow(flowConfig)) {
    return HELP_TOPIC_SHEET_LABELS;
  } else {
    return HELP_TOPIC_DIE_CUT_STICKERS;
  }
};

const artSizeAndPositionTitleKey = flowConfig => {
  if (isSticker(flowConfig)) {
    return HELP_TOPIC_STICKER_SIZE_POSITION;
  } else if (isCustomShapeSticker(flowConfig)) {
    return HELP_TOPIC_STICKER_SIZE;
  } else if (isSheetFlow(flowConfig)) {
    return HELP_TOPIC_SHEET_SIZE;
  } else {
    return HELP_TOPIC_ART_SIZE_POSITION;
  }
};

export const getAvailableHelpSections = ({flowConfig,matrix}) =>
  [
    {
      name: HELP_SECTION_PRODUCT_TYPE,
      titleKey: productSectionTypeTitleKey(flowConfig),
      subsections: [HELP_TOPIC_CONTACT_US],
      available: true,
      order: 0,
    },
    {
      name: HELP_SECTION_UPLOAD_ART,
      titleKey: HELP_TOPIC_UPLOAD_ART,
      subsections: [
          ...isSheetFlow(flowConfig) ? [HELP_TOPIC_UPLOAD_ART_PREPARE_FILES, HELP_TOPIC_UPLOAD_ART_LABEL_SHAPE_YOUR_UPLOAD ] : []
      ],
      available: true,
      order: 1,
    },
    {
      name: HELP_SECTION_ART_SIZE_POSITION,
      titleKey: artSizeAndPositionTitleKey(flowConfig),
      subsections: [
        !isCustomShapeSticker(flowConfig) && HELP_TOPIC_ART_SIZE_POSITION_STB_LINES,
        isSheetFlow(flowConfig) && HELP_TOPIC_SHAPE_CORNERS_SIZE,
      ],
      available: true,
      order: 2,
    },
    {
      name: HELP_SECTION_APPLY_CODES,
      titleKey: HELP_TOPIC_APPLY_CODES,
      hasSubsections: false,
      available: isDesignIn(flowConfig),
      order: 3,
    },
    {
      name: HELP_SECTION_WHITE_INK,
      titleKey: HELP_TOPIC_WHITE_INK,
      subsections: [HELP_TOPIC_WHITE_INK_OPTIONS, isSheetFlow(flowConfig) && HELP_TOPIC_WHITE_INK_IN_YOUR_UPLOAD],
      available: !isMobileMode(matrix.ctx) && !isRollFlow(flowConfig),
      order: 4,
    },
  ].filter(({ available }) => !!available);

export const getAvailableHelpTopics = availableHelpSections => {
  const getTopicsForSectionName = sectionName => {
    const { titleKey: topicKey, subsections = [] } = availableHelpSections.find(({ name }) => name === sectionName) || {};
    return [
      { topicKey, sectionName },
      ...subsections.filter(Boolean).map(topicKey => ({ topicKey, sectionName, isSubsection: true })),
    ];
  };

  const availableHelpTopics = availableHelpSections
    .sort(({ order: prev }, { order: next }) => prev - next)
    .map(({ name: sectionName }) => sectionName)
    .map(getTopicsForSectionName);

  return flattened(availableHelpTopics);
};

const workingAreaWarningAdjusted = {
  key: WORKING_AREA_WARNING_ADJUSTED_KEY,
  helpSection: HELP_SECTION_ART_SIZE_POSITION,
  helpTopic: HELP_TOPIC_ART_SIZE_POSITION_ADJUSTED,
};
const workingAreaWarningFlattened = {
  key: WORKING_AREA_WARNING_FLATTENED_KEY,
  helpSection: HELP_SECTION_ART_SIZE_POSITION,
  helpTopic: HELP_TOPIC_ART_SIZE_POSITION_FLATTENED,
};
const workingAreaWarningStretched = {
  key: WORKING_AREA_WARNING_STRETCHED_KEY,
  helpSection: HELP_SECTION_ART_SIZE_POSITION,
  helpTopic: HELP_TOPIC_ART_SIZE_POSITION_STRETCHED,
};
const sheetWorkingAreaWarningStretched = {
  key: WORKING_AREA_WARNING_STRETCHED_KEY,
  helpSection: HELP_SECTION_UPLOAD_ART,
  helpTopic: HELP_TOPIC_UPLOAD_ART_PREPARE_FILES,
};
const workingAreaWarningCustomShape = {
  key: WORKING_AREA_WARNING_CUSTOM_SHAPE_KEY,
  helpSection: HELP_SECTION_UPLOAD_ART,
  helpTopic: HELP_TOPIC_UPLOAD_ART_LABEL_SHAPE_YOUR_UPLOAD,
};
const workingAreaWarningHPIWhite = {
  key: WORKING_AREA_WARNING_HPI_WHITE_KEY,
  helpSection: HELP_SECTION_WHITE_INK,
  helpTopic: HELP_TOPIC_WHITE_INK_IN_YOUR_UPLOAD,
};
const workingAreaWarningCustomShapeHPIWhite = {
  key: WORKING_AREA_WARNING_CUSTOM_SHAPE_HPI_WHITE_KEY,
  helpSection: HELP_SECTION_UPLOAD_ART,
  helpTopic: HELP_TOPIC_UPLOAD_ART_LABEL_SHAPE_YOUR_UPLOAD,
};
const rollWorkingAreaWarningHPIWhite = {
  key: WORKING_AREA_WARNING_HPI_WHITE_KEY,
  helpSection: HELP_SECTION_PRODUCT_TYPE,
  helpTopic: HELP_TOPIC_PRODUCT_TYPE,
};
const rollWorkingAreaWarningCustomShapeHPIWhite = {
  key: WORKING_AREA_WARNING_CUSTOM_SHAPE_HPI_WHITE_KEY,
  helpSection: HELP_SECTION_PRODUCT_TYPE,
  helpTopic: HELP_TOPIC_PRODUCT_TYPE,
};
const workingAreaWarningOptimized = [
  ...WORKING_AREA_WARNING_OPTIMIZED_CODES.map(optimizedCode => ({
    [optimizedCode]: {
      key: WORKING_AREA_WARNING_OPTIMIZED_KEY,
      helpSection: HELP_SECTION_ART_SIZE_POSITION,
      helpTopic: HELP_TOPIC_ART_SIZE_POSITION_OPTIMIZED,
    },
  })),
];
export const getWorkingAreaWarningsList = (flowConfig) =>
  Object.assign(
    {
      [WORKING_AREA_WARNING_ADJUSTED_CODE]: workingAreaWarningAdjusted,
      [WORKING_AREA_WARNING_FLATTENED_CODE]: workingAreaWarningFlattened,
      [WORKING_AREA_WARNING_STRETCHED_CODE]: isSheetFlow(flowConfig) ? sheetWorkingAreaWarningStretched : workingAreaWarningStretched,
      [WORKING_AREA_WARNING_CUSTOM_SHAPE_CODE]: workingAreaWarningCustomShape,
      [WORKING_AREA_WARNING_HPI_WHITE_CODE]: isRollFlow(flowConfig)
        ? rollWorkingAreaWarningHPIWhite
        : workingAreaWarningHPIWhite,
      [WORKING_AREA_WARNING_CUSTOM_SHAPE_HPI_WHITE_CODE]: isRollFlow(flowConfig)
        ? rollWorkingAreaWarningCustomShapeHPIWhite
        : workingAreaWarningCustomShapeHPIWhite,
    },
    ...workingAreaWarningOptimized,
  );

export const setLabelTypePart = (labels, conditions) => {
  const index = conditions.findIndex((i) => i === true);
  return index !== -1 ? labels[index] : labels[labels.length - 1];
};
